import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import Icon from '../Icon/Icon';
import { fetchAdminNews } from '../../Reducers/actions/newsActions';
import { calculateReadingTime } from '../../../helpers/utilities';
import { formatEurFromCents } from '../../../helpers/money';
import './NewsTable.scss';

const ROWS_PER_PAGE = 18;
const COLUMNS = [
    { key: 'title', label: 'News', sortKey: 'title' },
    { key: 'creator_username', label: 'Creator username', sortKey: 'creator_username' },
    { key: 'read_time', label: 'Read time', sortKey: 'read_time' },
    { key: 'earnings', label: 'Earnings', sortKey: 'earnings' },
    { key: 'updated_at', label: 'Updated at', sortKey: 'updated_at' },
];

function formatReadTime(row) {
    if (row.read_time != null) {
        const mins = typeof row.read_time === 'number' ? row.read_time : parseInt(row.read_time, 10);
        return Number.isNaN(mins) ? '—' : `${mins}mins`;
    }
    if (row.content) {
        const mins = calculateReadingTime(row.content);
        return `${mins}mins`;
    }
    return '—';
}

function NewsTable({ onTotalCountChange }) {
    const navigate = useNavigate();
    const [news, setNews] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const latestRequestRef = useRef(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [showEditTableModal, setShowEditTableModal] = useState(false);
    const [columnVisibility, setColumnVisibility] = useState(
        COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
    );
    const [searchDebounce, setSearchDebounce] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const exportDropdownRef = React.useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
                setShowExportDropdown(false);
            }
        };
        if (showExportDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showExportDropdown]);

    useEffect(() => {
        const t = setTimeout(() => setSearchDebounce(searchQuery), 400);
        return () => clearTimeout(t);
    }, [searchQuery]);

    const fetchNewsData = useCallback(async () => {
        const requestId = ++latestRequestRef.current;
        const isStale = () => requestId !== latestRequestRef.current;
        setLoading(true);
        setLoadError(null);
        try {
            const response = await fetchAdminNews({
                page: currentPage,
                limit: ROWS_PER_PAGE,
                search: searchDebounce || undefined,
                sort_by: sortConfig.key || undefined,
                sort_dir: sortConfig.key ? sortConfig.direction : undefined,
            });
            if (isStale()) return;
            if (response?.success && response?.data) {
                const data = response.data;
                const list = Array.isArray(data) ? data : (data.news || data.items || []);
                const totalCount = typeof data.total === 'number' ? data.total : list.length;
                setNews(list);
                setTotal(totalCount);
                onTotalCountChange?.(totalCount);
            } else {
                throw new Error(response?.message || 'Failed to load news');
            }
        } catch {
            if (isStale()) return;
            // Surface the failure instead of rendering it as "0 of 0 / No news found".
            setNews([]);
            setTotal(0);
            setLoadError('Could not load news. Please try again.');
        } finally {
            if (!isStale()) setLoading(false);
        }
    }, [currentPage, searchDebounce, sortConfig, onTotalCountChange]);

    useEffect(() => {
        fetchNewsData();
    }, [fetchNewsData]);

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
        setCurrentPage(1);
    };

    const handleSearchSubmit = (e) => {
        e?.preventDefault();
        setCurrentPage(1);
        fetchNewsData();
    };

    const handleSort = (key) => {
        const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction });
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(Math.max(1, Math.min(pageNumber, totalPages)));
    };

    const totalPages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));
    const indexOfFirst = (currentPage - 1) * ROWS_PER_PAGE;
    const indexOfLast = Math.min(indexOfFirst + ROWS_PER_PAGE, total);

    const getSortIcon = (key) => (
        <Icon
            alias="arrow-up-down"
            width={16}
            height={16}
            color={sortConfig.key !== key ? '#0A0F1E' : '#FF3232'}
        />
    );

    const handleExport = () => {
        setShowExportDropdown(false);
        // TODO: Implement export when backend supports it
    };

    if (loading && news.length === 0) {
        return (
            <div className="news-table hyk-table">
                <div className="news-table-loading">Loading news…</div>
            </div>
        );
    }

    return (
        <div className="news-table hyk-table">
            <div className="table-header">
                <div className="table-header-left">
                    <form className="search-box" onSubmit={handleSearchSubmit}>
                        <input
                            type="text"
                            placeholder="Search for news"
                            value={searchQuery}
                            onChange={handleSearch}
                            aria-label="Search news"
                        />
                        {searchQuery && !loadError && (
                            <span className="search-count" style={{ right: '40px' }}>
                                {total} results
                            </span>
                        )}
                        <Icon
                            style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}
                            alias="search"
                            width={20}
                            height={20}
                            color="#0A0F1E"
                        />
                    </form>
                </div>
                <div className="table-header-right">
                    <div className="header-pagination">
                        <button
                            type="button"
                            className="pagination-arrow"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            aria-label="Previous page"
                        >
                            <Icon alias="chevron-left" width={16} height={16} color={currentPage === 1 ? '#ccc' : '#0A0F1E'} />
                        </button>
                        <span className="pagination-info">
                            {loadError ? '—' : total === 0 ? '0 of 0' : `${indexOfFirst + 1}-${indexOfLast} of ${total}`}
                        </span>
                        <button
                            type="button"
                            className="pagination-arrow"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages}
                            aria-label="Next page"
                        >
                            <Icon alias="chevron-right" width={16} height={16} color={currentPage >= totalPages ? '#ccc' : '#0A0F1E'} />
                        </button>
                    </div>
                    <div className="header-actions">
                        <button
                            type="button"
                            className="header-action-btn"
                            onClick={() => setShowEditTableModal(true)}
                            aria-label="Edit table"
                        >
                            <Icon alias="plus" width={16} height={16} color="#0A0F1E" />
                            <span>Edit table</span>
                        </button>
                        <div className="export-dropdown-wrapper" ref={exportDropdownRef}>
                            <button
                                type="button"
                                className="header-action-btn"
                                onClick={() => setShowExportDropdown(!showExportDropdown)}
                                aria-label="Export"
                                aria-expanded={showExportDropdown}
                            >
                                <span>Export</span>
                                <Icon alias="chevron-down" width={16} height={16} color="#0A0F1E" />
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown">
                                    <button
                                        type="button"
                                        onClick={handleExport}
                                        aria-label="Export as CSV"
                                    >
                                        Export as CSV
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {loadError ? (
                <div className="news-table-error" role="alert">
                    <span>{loadError}</span>
                    <button type="button" className="news-table-retry" onClick={fetchNewsData}>
                        Retry
                    </button>
                </div>
            ) : news.length === 0 ? (
                <div className="news-table-empty">No news found.</div>
            ) : (
                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                {COLUMNS.filter((c) => columnVisibility[c.key]).map((col) => (
                                    <th
                                        key={col.key}
                                        onClick={() => handleSort(col.sortKey)}
                                        className="sortable"
                                    >
                                        <div className="d-flex f-row aic jcsb">
                                            {col.label}
                                            {getSortIcon(col.sortKey)}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {news.map((row, index) => {
                                const newsId = row.news_id || row.id;
                                const creatorId = row.creator_id || row.creator?.user_id;
                                const creatorUsername = row.creator_username ?? row.creator?.userName ?? '';
                                const displayUsername = creatorUsername.startsWith('@') ? creatorUsername : `@${creatorUsername}`;
                                const title = row.title || row.news_title || '—';

                                return (
                                    <tr key={newsId || index}>
                                        {columnVisibility.title && (
                                            <td>
                                                <button
                                                    type="button"
                                                    className="news-link"
                                                    onClick={() => navigate(`/admin/news/${newsId}`)}
                                                >
                                                    {title}
                                                </button>
                                            </td>
                                        )}
                                        {columnVisibility.creator_username && (
                                            <td>
                                                {creatorId ? (
                                                    <button
                                                        type="button"
                                                        className="news-link"
                                                        onClick={() => navigate(`/admin/users/${creatorId}`)}
                                                    >
                                                        {displayUsername || '—'}
                                                    </button>
                                                ) : (
                                                    <span>{displayUsername || '—'}</span>
                                                )}
                                            </td>
                                        )}
                                        {columnVisibility.read_time && (
                                            <td>{formatReadTime(row)}</td>
                                        )}
                                        {columnVisibility.earnings && (
                                            <td>{formatEurFromCents(row.earnings)}</td>
                                        )}
                                        {columnVisibility.updated_at && (
                                            <td>{row.updated_at ? moment(row.updated_at).format('DD/MM/YYYY') : '—'}</td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {showEditTableModal && (
                <div className="edit-table-modal-overlay" role="dialog" aria-modal="true" aria-label="Edit table columns">
                    <div className="edit-table-modal">
                        <div className="edit-table-header">
                            <h2>Edit table</h2>
                            <button
                                type="button"
                                className="close-btn"
                                onClick={() => setShowEditTableModal(false)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className="edit-table-content">
                            <div className="edit-table-section">
                                <span className="section-label">Columns</span>
                                <div className="columns-list">
                                    {COLUMNS.map((col) => (
                                        <label key={col.key} className="column-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={columnVisibility[col.key]}
                                                onChange={(e) =>
                                                    setColumnVisibility((prev) => ({ ...prev, [col.key]: e.target.checked }))
                                                }
                                            />
                                            <span className="checkbox-custom" />
                                            <span className="checkbox-label">{col.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="edit-table-actions">
                            <button type="button" className="cancel-btn" onClick={() => setShowEditTableModal(false)}>
                                Cancel
                            </button>
                            <button type="button" className="save-btn" onClick={() => setShowEditTableModal(false)}>
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NewsTable;
